import React, { useState, useEffect } from 'react';
import Card from "components/card";
import { MdCheckCircle, MdCancel, MdAccessTime, MdSupervisorAccount, MdTableChart, MdBarcode, MdDateRange } from "react-icons/md";
import UsibrasToast from '../../../components/UsibrasToast';

const qualityRows = [
  'First',
  'Second',
  'Third',
  'Spotted',
  'W3M',
  'Rotten',
  'W3D',
  'SK1',
  'SK3',
];

const defectRows = [
  'Peels',
  'Dirty',
  'Clean',
  'Immature',
  'Broken',
  'Cut',
  'Oily',
  'Pieces',
  'Butts',
];

const productTypes = [
  'WW 180',
  'W2 180',
  'WW 210',
  'W2 210',
  'WW 240',
  'W2 240',
  'WW 320',
  'W2 320',
  'WW 360',
  'W2 360',
  'W3',
  'SK1',
  'SK2',
  'SK3',
  'W3M',
  'B1',
  'B2',
  'B3',
  'WW 210s',
  'WW 240s',
  'WW 320s',
  'WW 360s',
  'WW 450',
  'W2 450',
  'WS',
  'WW 450s'
];

const supervisorNames = [
  'Evelyn Awudi',
  'Leticia Owusu',
  'Joyce Bebblin'
];

const analystNames = [
  'Felicity Baidoo',
  'Constance Bofah',
  'Nadia Issaka',
  'Yayra Dogbe',
  'Abigail Abbey',
  'Priscilla Andoh',
  'Esther Essuman'
];

const batchCodePrefixes = ['BN', 'BC', 'BR', 'BS','BK'];

const AnalystEntry2 = () => {
  const [form, setForm] = useState({
    product: '',
    table: '',
    batchCode: '',
    batchCodePrefix: '',
    batchCodeYear: '',
    batchCodeWeek: '',
    supervisor: '',
    analyst: '',
    numMonoblocks: '',
    count: '',
    moisture: '',
    weight: '',
    qualityPercentages: Array(qualityRows.length).fill(''),
    defectPercentages: Array(defectRows.length).fill(''),
    recommendation: '',
    rework: '',
    observation: '',
    date: '',
    qualityControl: '',
    quantityBoxes: '',
    sectorSupervisor: '',
    qualityManager: ''
  });

  const [manualOverride, setManualOverride] = useState({
    enabled: false,
    status: null,
    reason: ''
  });

  // Function to get current year and week
  const getCurrentYearAndWeek = () => {
    const now = new Date();
    const year = now.getFullYear().toString().slice(-2); // Get last 2 digits of year
    const week = Math.ceil((now.getDate() + new Date(now.getFullYear(), 0, 1).getDay()) / 7);
    return { year, week: week.toString().padStart(2, '0') };
  };

  // Function to generate batch code
  const generateBatchCode = (prefix, year, week) => {
    return `${prefix}/${year}/${week}`;
  };

  // Function to handle batch code prefix change
  const handleBatchCodePrefixChange = (prefix) => {
    const { year, week } = getCurrentYearAndWeek();
    const newBatchCode = generateBatchCode(prefix, year, week);
    
    setForm(prev => ({
      ...prev,
      batchCodePrefix: prefix,
      batchCodeYear: year,
      batchCodeWeek: week,
      batchCode: newBatchCode
    }));
  };

  // Function to handle manual batch code editing
  const handleBatchCodeChange = (value) => {
    setForm(prev => ({
      ...prev,
      batchCode: value
    }));
  };



  const handleInput = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleQualityPercentageChange = (idx, value) => {
    setForm(prev => ({
      ...prev,
      qualityPercentages: prev.qualityPercentages.map((v, i) => i === idx ? value : v)
    }));
  };

  const handleDefectPercentageChange = (idx, value) => {
    setForm(prev => ({
      ...prev,
      defectPercentages: prev.defectPercentages.map((v, i) => i === idx ? value : v)
    }));
  };

  const handleRework = (value) => {
    setForm(prev => ({ ...prev, rework: value }));
  };

  // Quality standards for 1st quality products (excluding ***** fields)
  const qualityStandards = {
    'Second': 5.0,
    'Third': 1.5,
    'Spotted': 1.5,
    'W3M': 0.5,
    'Rotten': 0.0,
    'Peels': 4.0, // TESTA is the same as Peels
    'Dirty': 4.0,
    'Broken': 8.0,
    'Cut': 1.5,
    'Oily': 2.0,
    'Dessert': 2.0
  };

  // Quality standards for 2nd quality products (W2 products)
  const qualityStandards2nd = {
    'First': 5.0,
    'Third': 5.0,
    'Spotted': 2.0,
    'W3M': 2.5,
    'Rotten': 0.0,
    'Peels': 4.0, // TESTA is the same as Peels
    'Dirty': 4.0,
    'Broken': 8.0,
    'Cut': 1.5,
    'Oily': 3.0,
    'Dessert': 2.0
  };

  // Quality standards for 3rd quality products (W3 products)
  const qualityStandards3rd = {
    'First': 5.0, // From the image, 1° Quality has 5.0% for W3
    'Second': 5.0,
    'Spotted': 2.0,
    'W3M': 2.5,
    'Rotten': 0.0,
    'Peels': 4.0, // TESTA is the same as Peels
    'Dirty': 4.0,
    'Broken': 8.0,
    'Cut': 3.0,
    'Immature': 1.5,
    'Oily': 3.0,
    'Dessert': 2.0
  };

  // Quality standards for SK1 products (4th quality)
  const qualityStandardsSK1 = {
    'First': 10.0,
    'Second': 10.0,
    'Third': 10.0,
    'Spotted': 10.0,
    'W3M': 10.0,
    'Rotten': 10.0,
    'W3D': 10.0,
    'SK1': 10.0,
    'SK3': 10.0,
    'Peels': 11.0,
    'Dirty': 11.0,
    'Clean': 10.0,
    'Immature': 10.0,
    'Broken': 11.0,
    'Cut': 10.0,
    'Oily': 10.0,
    'Dessert': 10.0,
    'Pieces': 8.0,
    'Butts': 8.0
  };

  // Quality standards for SK3 products (4th quality)
  const qualityStandardsSK3 = {
    'First': 10.0,
    'Second': 10.0,
    'Third': 10.0,
    'Spotted': 10.0,
    'W3M': 10.0,
    'Rotten': 10.0,
    'W3D': 10.0,
    'SK1': 10.0,
    'SK3': 10.0,
    'Peels': 11.0,
    'Dirty': 11.0,
    'Clean': 10.0,
    'Immature': 10.0,
    'Broken': 11.0,
    'Cut': 10.0,
    'Oily': 10.0,
    'Dessert': 10.0,
    'Pieces': 8.0,
    'Butts': 8.0
  };

  // Function to check count assessment (separate from quality standards)
  const checkCountAssessment = () => {
    const countResults = [];
    
    // Only check count assessment if count value is provided
    if (form.count && form.product) {
      const countValue = parseFloat(form.count) * 4; // Multiply by 4 as per the formula
      
      // Determine if it's low or high count based on the count value
      if (form.product === 'WW 240' || form.product === 'W2 240') {
        // For 240 products, check if it's within the high count range
        if (countValue >= 242 && countValue <= 268) {
          countResults.push({
            field: 'Count (High)',
            value: `${countValue.toFixed(0)}`,
            threshold: '242-268',
            status: 'passed',
            rawValue: countValue
          });
        }
      } else if (form.product === 'WW 320' || form.product === 'W2 320') {
        // For 320 products, check low count range (270-288) and high count range (322-338)
        if (countValue >= 270 && countValue <= 288) {
          countResults.push({
            field: 'Count (Low)',
            value: `${countValue.toFixed(0)}`,
            threshold: '270-288',
            status: 'passed',
            rawValue: countValue
          });
        } else if (countValue >= 322 && countValue <= 338) {
          countResults.push({
            field: 'Count (High)',
            value: `${countValue.toFixed(0)}`,
            threshold: '322-338',
            status: 'passed',
            rawValue: countValue
          });
        }
      } else if (form.product === 'WW 360' || form.product === 'W2 360') {
        // For 360 products, check if it's within the high count range
        if (countValue >= 362 && countValue <= 398) {
          countResults.push({
            field: 'Count (High)',
            value: `${countValue.toFixed(0)}`,
            threshold: '362-398',
            status: 'passed',
            rawValue: countValue
          });
        }
      } else if (form.product === 'WW 450' || form.product === 'W2 450') {
        // For 450 products, check if it's within the high count range
        if (countValue >= 452 && countValue <= 498) {
          countResults.push({
            field: 'Count (High)',
            value: `${countValue.toFixed(0)}`,
            threshold: '452-498',
            status: 'passed',
            rawValue: countValue
          });
        }
      }
    }
    
    return countResults;
  };

  // Function to check broken assessment (separate from quality standards)
  const checkBrokenAssessment = () => {
    const brokenResults = [];
    
    const brokenIndex = defectRows.indexOf('Broken');
    if (brokenIndex !== -1 && form.defectPercentages[brokenIndex] && form.product) {
      const brokenRawValue = parseFloat(form.defectPercentages[brokenIndex]) || 0;
      const brokenPercentage = (brokenRawValue / 454) * 100;
      
      // Determine if it's low broken (≤ 3%) or high broken (≥ 8%)
      if (brokenPercentage <= 3) {
        brokenResults.push({
          field: 'Broken (Low)',
          value: `${brokenPercentage.toFixed(2)}%`,
          threshold: '≤ 3%',
          status: 'passed',
          rawValue: brokenPercentage
        });
      } else if (brokenPercentage >= 8) {
        brokenResults.push({
          field: 'Broken (High)',
          value: `${brokenPercentage.toFixed(2)}%`,
          threshold: '≥ 8%',
          status: 'passed',
          rawValue: brokenPercentage
        });
      }
    }
    
    return brokenResults;
  };

  // Function to check if product meets quality standards
  const checkQualityStandards = () => {
    const failedFields = [];
    
    // Don't check if no product is selected
    if (!form.product) {
      return {
        status: 'pending',
        message: 'Please select a product to assess quality standards',
        failedFields: []
      };
    }
    
    // Determine which standards to use based on product type
    let standardsToUse;
    let productType;
    
    if (form.product.startsWith('WW')) {
      standardsToUse = qualityStandards;
      productType = '1st quality';
    } else if (form.product.startsWith('W2')) {
      standardsToUse = qualityStandards2nd;
      productType = '2nd quality';
    } else if (form.product.startsWith('W3')) {
      standardsToUse = qualityStandards3rd;
      productType = '3rd quality';
    } else if (form.product === 'SK1') {
      standardsToUse = qualityStandardsSK1;
      productType = '4th quality (SK1)';
    } else if (form.product === 'SK3') {
      standardsToUse = qualityStandardsSK3;
      productType = '4th quality (SK3)';
    } else {
      return {
        status: 'approved',
        message: 'Quality standards only apply to 1st, 2nd, 3rd, and 4th quality products',
        failedFields: []
      };
    }



    // Check if any data has been entered
    const hasQualityData = form.qualityPercentages.some(value => value !== '');
    const hasDefectData = form.defectPercentages.some(value => value !== '');
    
    if (!hasQualityData && !hasDefectData) {
      return {
        status: 'pending',
        message: 'Please enter analysis data to assess quality standards',
        failedFields: []
      };
    }

    // Check quality percentages (excluding ***** fields)
    qualityRows.forEach((quality, index) => {
      const rawValue = parseFloat(form.qualityPercentages[index]) || 0;
      const standard = standardsToUse[quality];
      
      // Only validate fields that have defined standards (exclude ***** fields)
      if (standard !== undefined) {
        // Convert grams to percentage: (grams / 454) * 100
        const percentageValue = (rawValue / 454) * 100;
        
        // These should not exceed the standard
        if (percentageValue > standard) {
          failedFields.push({
            field: quality,
            value: `${percentageValue.toFixed(2)}%`,
            threshold: `${standard}%`,
            type: 'quality',
            rawValue: rawValue
          });
        }
      }
    });

    // Check defect percentages (excluding ***** fields)
    defectRows.forEach((defect, index) => {
      const rawValue = parseFloat(form.defectPercentages[index]) || 0;
      const standard = standardsToUse[defect];
      
      // Only validate fields that have defined standards (exclude ***** fields)
      if (standard !== undefined) {
        // Convert grams to percentage: (grams / 454) * 100
        const percentageValue = (rawValue / 454) * 100;
        
        // These should not exceed the standard
        if (percentageValue > standard) {
          failedFields.push({
            field: defect,
            value: `${percentageValue.toFixed(2)}%`,
            threshold: `${standard}%`,
            type: 'defect',
            rawValue: rawValue
          });
        }
      }
    });

    if (failedFields.length >= 1) {
      return {
        status: 'rejected',
        message: 'Product does not meet quality standards - Fields exceed thresholds',
        failedFields
      };
    } else {
      return {
        status: 'approved',
        message: `Product meets all ${productType} quality standards`,
        failedFields: []
      };
    }
  };

  // Get current recommendation based on form data
  const autoRecommendation = checkQualityStandards();
  const countAssessment = checkCountAssessment();
  const brokenAssessment = checkBrokenAssessment();
  
  // Apply manual override if enabled
  const recommendation = manualOverride.enabled && manualOverride.status 
    ? {
        status: manualOverride.status,
        message: manualOverride.reason || `Manually ${manualOverride.status === 'approved' ? 'approved' : 'rejected'} by analyst`,
        failedFields: manualOverride.status === 'approved' ? [] : autoRecommendation.failedFields
      }
    : autoRecommendation;

  // Success notification component
  const SuccessNotification = () => (
    <div className="fixed top-4 right-4 z-50 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-2">
      <MdCheckCircle className="text-xl" />
      <span>Analysis submitted successfully!</span>
    </div>
  );

  // Print function for thermal printer
  const printReceipt = (formData) => {
    try {
      // Create a new window for printing
      const printWindow = window.open('', '_blank', 'width=300,height=600');
      
      // Check if popup was blocked
      if (!printWindow) {
        console.warn('Popup blocked by browser. Using fallback print method.');
        // Fallback: print current page with receipt content
        printReceiptFallback(formData);
        return;
      }
      
      // Format date
      const formattedDate = new Date(formData.date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });

      // Create the receipt HTML
      const receiptHTML = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>Analysis Receipt</title>
            <style>
              @media print {
                @page {
                  size: 80mm auto;
                  margin: 0;
                }
                body {
                  margin: 0;
                  padding: 10px;
                  font-family: 'Courier New', monospace;
                  font-size: 12px;
                  line-height: 1.2;
                  width: 80mm;
                }
              }
              body {
                font-family: 'Courier New', monospace;
                font-size: 12px;
                line-height: 1.2;
                width: 80mm;
                margin: 0;
                padding: 10px;
                background: white;
              }
              .header {
                text-align: center;
                border-bottom: 1px dashed #000;
                padding-bottom: 10px;
                margin-bottom: 10px;
              }
              .company-name {
                font-size: 14px;
                font-weight: bold;
                margin-bottom: 5px;
              }
              .receipt-title {
                font-size: 12px;
                margin-bottom: 5px;
              }
              .info-row {
                display: flex;
                justify-content: space-between;
                margin-bottom: 5px;
              }
              .label {
                font-weight: bold;
              }
              .value {
                text-align: right;
              }
              .recommendation {
                text-align: center;
                margin: 15px 0;
                padding: 10px;
                border: 2px solid #000;
                border-radius: 5px;
              }
              .approved {
                background-color: #d4edda;
                border-color: #28a745;
              }
              .rejected {
                background-color: #f8d7da;
                border-color: #dc3545;
              }
              .footer {
                text-align: center;
                border-top: 1px dashed #000;
                padding-top: 10px;
                margin-top: 15px;
                font-size: 10px;
              }
              .divider {
                border-top: 1px dashed #000;
                margin: 10px 0;
              }
            </style>
          </head>
          <body>
            <div class="header">
              <div class="company-name">USIBRAS GHANA LIMITED</div>
              <div class="receipt-title">PRODUCT ANALYSIS REPORT (WHOLES)</div>
            </div>

            <div class="info-row">
              <span class="label">Product:</span>
              <span class="value">${formData.product}</span>
            </div>

            <div class="info-row">
              <span class="label">Date:</span>
              <span class="value">${formattedDate}</span>
            </div>

            <div class="info-row">
              <span class="label">Batch Code:</span>
              <span class="value">${formData.batchCode}</span>
            </div>

            <div class="info-row">
              <span class="label">Supervisor:</span>
              <span class="value">${formData.supervisor}</span>
            </div>

            <div class="divider"></div>

            ${formData.count ? `
            <div class="info-row">
              <span class="label">Count:</span>
              <span class="value">${formData.count}</span>
            </div>
            ` : ''}

            ${formData.moisture ? `
            <div class="info-row">
              <span class="label">Moisture (%):</span>
              <span class="value">${formData.moisture}%</span>
            </div>
            ` : ''}

            <div class="divider"></div>

            <div class="recommendation ${recommendation.status === 'approved' ? 'approved' : 'rejected'}">
              <div style="font-size: 14px; font-weight: bold; margin-bottom: 5px;">
                RECOMMENDATION
              </div>
              <div style="font-size: 16px; font-weight: bold;">
                ${recommendation.status === 'approved' ? '✓ APPROVED' : '✗ REJECTED'}
              </div>
            </div>

            <div class="divider"></div>

            <div class="info-row">
              <span class="label">Quality Manager:</span>
              <span class="value">Gifty Asante</span>
            </div>

            <div class="footer">
              <div>Generated on: ${new Date().toLocaleString()}</div>
              <div>USIBRAS Ghana Limited</div>
              <div>Quality Control Department</div>
            </div>
          </body>
        </html>
      `;

      // Write the HTML to the new window
      printWindow.document.write(receiptHTML);
      printWindow.document.close();

      // Wait for content to load, then print
      printWindow.onload = function() {
        printWindow.print();
        printWindow.close();
      };

      // Fallback if onload doesn't work
      setTimeout(() => {
        if (printWindow && printWindow.document && printWindow.document.readyState === 'complete') {
          printWindow.print();
          printWindow.close();
        }
      }, 500);
      
    } catch (error) {
      console.error('Error in printReceipt:', error);
      // Fallback to alternative print method
      printReceiptFallback(formData);
    }
  };

  // Fallback print function
  const printReceiptFallback = (formData) => {
    try {
      // Format date
      const formattedDate = new Date(formData.date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });

      // Create a temporary div with the receipt content
      const receiptDiv = document.createElement('div');
      receiptDiv.style.cssText = `
        position: fixed;
        top: -9999px;
        left: -9999px;
        width: 80mm;
        font-family: 'Courier New', monospace;
        font-size: 12px;
        line-height: 1.2;
        background: white;
        padding: 10px;
        border: 1px solid #000;
      `;
      
      receiptDiv.innerHTML = `
        <div style="text-align: center; border-bottom: 1px dashed #000; padding-bottom: 10px; margin-bottom: 10px;">
          <div style="font-size: 14px; font-weight: bold; margin-bottom: 5px;">USIBRAS GHANA LIMITED</div>
          <div style="font-size: 12px; margin-bottom: 5px;">PRODUCT ANALYSIS REPORT (WHOLES)</div>
        </div>

        <div style="display: flex; justify-content: space-between; margin-bottom: 5px;">
          <span style="font-weight: bold;">Product:</span>
          <span>${formData.product}</span>
        </div>

        <div style="display: flex; justify-content: space-between; margin-bottom: 5px;">
          <span style="font-weight: bold;">Date:</span>
          <span>${formattedDate}</span>
        </div>

        <div style="display: flex; justify-content: space-between; margin-bottom: 5px;">
          <span style="font-weight: bold;">Batch Code:</span>
          <span>${formData.batchCode}</span>
        </div>

        <div style="display: flex; justify-content: space-between; margin-bottom: 5px;">
          <span style="font-weight: bold;">Supervisor:</span>
          <span>${formData.supervisor}</span>
        </div>

        <div style="border-top: 1px dashed #000; margin: 10px 0;"></div>

        ${formData.count ? `
        <div style="display: flex; justify-content: space-between; margin-bottom: 5px;">
          <span style="font-weight: bold;">Count:</span>
          <span>${formData.count}</span>
        </div>
        ` : ''}

        ${formData.moisture ? `
        <div style="display: flex; justify-content: space-between; margin-bottom: 5px;">
          <span style="font-weight: bold;">Moisture (%):</span>
          <span>${formData.moisture}%</span>
        </div>
        ` : ''}

        <div style="border-top: 1px dashed #000; margin: 10px 0;"></div>

        <div style="text-align: center; margin: 15px 0; padding: 10px; border: 2px solid #000; border-radius: 5px; background-color: ${recommendation.status === 'approved' ? '#d4edda' : '#f8d7da'}; border-color: ${recommendation.status === 'approved' ? '#28a745' : '#dc3545'};">
          <div style="font-size: 14px; font-weight: bold; margin-bottom: 5px;">RECOMMENDATION</div>
          <div style="font-size: 16px; font-weight: bold;">${recommendation.status === 'approved' ? '✓ APPROVED' : '✗ REJECTED'}</div>
        </div>

        <div style="border-top: 1px dashed #000; margin: 10px 0;"></div>

        <div style="display: flex; justify-content: space-between; margin-bottom: 5px;">
          <span style="font-weight: bold;">Quality Manager:</span>
          <span>Gifty Asante</span>
        </div>

        <div style="text-align: center; border-top: 1px dashed #000; padding-top: 10px; margin-top: 15px; font-size: 10px;">
          <div>Generated on: ${new Date().toLocaleString()}</div>
          <div>USIBRAS Ghana Limited</div>
          <div>Quality Control Department</div>
        </div>
      `;

      // Add to document
      document.body.appendChild(receiptDiv);

      // Print
      window.print();

      // Remove from document after printing
      setTimeout(() => {
        document.body.removeChild(receiptDiv);
      }, 1000);

    } catch (error) {
      console.error('Error in printReceiptFallback:', error);
    }
  };

  const getProductQuality = (product) => {
    if (!product) return null;
    
    if (product.startsWith('WW')) return { quality: '1st Quality', color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' };
    if (product.startsWith('W2')) return { quality: '2nd Quality', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' };
    if (product.startsWith('W3') || product.startsWith('WS')) return { quality: '3rd Quality', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' };
    if (product.startsWith('SK')) return { quality: '4th Quality', color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' };
    
    return null;
  };

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'info' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setToast({ show: false, message: '', type: 'info' });

    // Require selecting Approve or Reject when Manual Override is enabled
    if (manualOverride.enabled && !manualOverride.status) {
      setIsSubmitting(false);
      setToast({
        show: true,
        message: 'Select Approve or Reject when Manual Override is enabled.',
        type: 'error'
      });
      return;
    }

    try {
      // Prepare the data according to the API specification
      const apiData = {
        product: form.product,
        batch_code: form.batchCode,
        supervisor: form.supervisor,
        analyst: form.analyst,
        num_monoblocks: parseInt(form.numMonoblocks),
        count_value: form.count ? parseFloat(form.count) * 4 : null, // Calculated value (count * 4) - optional
        moisture_percentage: form.moisture ? parseFloat(form.moisture) : null, // Optional
        weigh_kg: form.weight ? parseFloat(form.weight) : null, // Optional
        
        // Quality percentages (convert from grams to percentage)
        first_quality_percentage: form.qualityPercentages[0] ? (parseFloat(form.qualityPercentages[0]) / 454) * 100 : 0,
        second_quality_percentage: form.qualityPercentages[1] ? (parseFloat(form.qualityPercentages[1]) / 454) * 100 : 0,
        third_quality_percentage: form.qualityPercentages[2] ? (parseFloat(form.qualityPercentages[2]) / 454) * 100 : 0,
        spotted_percentage: form.qualityPercentages[3] ? (parseFloat(form.qualityPercentages[3]) / 454) * 100 : 0,
        w3m_percentage: form.qualityPercentages[4] ? (parseFloat(form.qualityPercentages[4]) / 454) * 100 : 0,
        rotten_percentage: form.qualityPercentages[5] ? (parseFloat(form.qualityPercentages[5]) / 454) * 100 : 0,
        w3d_percentage: form.qualityPercentages[6] ? (parseFloat(form.qualityPercentages[6]) / 454) * 100 : 0,
        sk1_percentage: form.qualityPercentages[7] ? (parseFloat(form.qualityPercentages[7]) / 454) * 100 : 0,
        sk3_percentage: form.qualityPercentages[8] ? (parseFloat(form.qualityPercentages[8]) / 454) * 100 : 0,
        
        // Defect percentages (convert from grams to percentage)
        peels_percentage: form.defectPercentages[0] ? (parseFloat(form.defectPercentages[0]) / 454) * 100 : 0,
        dirty_percentage: form.defectPercentages[1] ? (parseFloat(form.defectPercentages[1]) / 454) * 100 : 0,
        clean_percentage: form.defectPercentages[2] ? (parseFloat(form.defectPercentages[2]) / 454) * 100 : 0,
        immature_percentage: form.defectPercentages[3] ? (parseFloat(form.defectPercentages[3]) / 454) * 100 : 0,
        broken_percentage: form.defectPercentages[4] ? (parseFloat(form.defectPercentages[4]) / 454) * 100 : 0,
        cut_percentage: form.defectPercentages[5] ? (parseFloat(form.defectPercentages[5]) / 454) * 100 : 0,
        oily_percentage: form.defectPercentages[6] ? (parseFloat(form.defectPercentages[6]) / 454) * 100 : 0,
        pieces_percentage: form.defectPercentages[7] ? (parseFloat(form.defectPercentages[7]) / 454) * 100 : 0,
        butts_percentage: form.defectPercentages[8] ? (parseFloat(form.defectPercentages[8]) / 454) * 100 : 0,
        
        // Additional percentage fields (these are the same as above but included as requested)
        pieces_percentage: form.defectPercentages[7] ? (parseFloat(form.defectPercentages[7]) / 454) * 100 : 0,
        sk1_percentage: form.qualityPercentages[7] ? (parseFloat(form.qualityPercentages[7]) / 454) * 100 : 0,
        sk3_percentage: form.qualityPercentages[8] ? (parseFloat(form.qualityPercentages[8]) / 454) * 100 : 0,
        butts_percentage: form.defectPercentages[8] ? (parseFloat(form.defectPercentages[8]) / 454) * 100 : 0,
        
        // Assessment status and message
        assessment_status: recommendation.status,
        assessment_message: recommendation.message,
        automatic_assessment: autoRecommendation.status,
        manual_override: manualOverride.enabled,
        manual_override_status: manualOverride.enabled ? manualOverride.status : null,
        manual_override_reason: manualOverride.enabled ? manualOverride.reason : null,
        
        // Assessment results - use the actual assessment values (optional)
        count_assessment: countAssessment.length > 0 ? countAssessment[0].field : (form.count ? "fail" : null),
        broken_assessment: brokenAssessment.length > 0 ? brokenAssessment[0].field : "fail",
        
        // Additional fields
        observation: form.observation,
        rework: form.rework,
        analysis_date: form.date,
        quality_manager: "Gifty Asante"
      };

      // Console log the API parameters to be sent
      // console.log('API Parameters to be sent:', JSON.stringify(apiData, null, 2));

      const response = await fetch('https://usibras-web-backend.onrender.com/api/analyst-wholes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(apiData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      setShowSuccess(true);
      setToast({
        show: true,
        message: result.message || 'Analysis submitted successfully!',
        type: 'success'
      });
      
      // printReceipt(form); // Call print function after successful submission
      
      // Reset form after successful submission
      setTimeout(() => {
        setForm({
          product: '',
          table: '',
          batchCode: '',
          batchCodePrefix: '',
          batchCodeYear: '',
          batchCodeWeek: '',
          supervisor: '',
          analyst: '',
          numMonoblocks: '',
          count: '',
          moisture: '',
          weight: '',
          qualityPercentages: Array(qualityRows.length).fill(''),
          defectPercentages: Array(defectRows.length).fill(''),
          recommendation: '',
          rework: '',
          observation: '',
          date: '',
          qualityControl: '',
          quantityBoxes: '',
          sectorSupervisor: '',
          qualityManager: ''
        });
        setManualOverride({
          enabled: false,
          status: null,
          reason: ''
        });
        setShowSuccess(false);
        setToast({ show: false, message: '', type: 'info' });
      }, 3000);

    } catch (error) {
      console.error('Error submitting analysis:', error);
      setToast({
        show: true,
        message: 'Data Entry failed. Please try again.',
        type: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="mt-2 sm:mt-5 h-full flex justify-center px-2 sm:px-4 lg:px-6">
      {/* Toast Notification */}
      {toast.show && (
        <UsibrasToast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(prev => ({ ...prev, show: false }))}
          position="top-right"
          duration={3000}
        />
      )}
      
      {/* Success Notification */}
      {showSuccess && <SuccessNotification />}
      
      <Card extra="relative w-full max-w-4xl p-4 sm:p-6 lg:p-8">
        <div className="absolute top-4 right-4">
          <span className="inline-block bg-gray-100 dark:bg-navy-800 text-navy-700 dark:text-white text-xs font-bold px-2 py-1 rounded shadow-sm">QC-FR-008</span>
        </div>
        <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8">
          {/* Header */}
          <div className="mb-4">
            <h1 className="text-xl sm:text-2xl font-bold text-navy-700 dark:text-white mb-1">Product Analysis Entry(Wholes)</h1>
            {/* <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">QC-FR-020</p> */}
          </div>

          {/* Product Info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            <div className="col-span-1 sm:col-span-2 lg:col-span-1">
              <label className="block text-sm font-medium mb-1">Product</label>
              <select 
                name="product" 
                value={form.product} 
                onChange={handleInput} 
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:bg-navy-700 dark:text-white" 
              >
                <option value="">Select a product type</option>
                {productTypes.map((productType) => (
                  <option key={productType} value={productType}>
                    {productType}
                  </option>
                ))}
              </select>
              {form.product && getProductQuality(form.product) && (
                <div className="mt-2">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getProductQuality(form.product).color}`}>
                    <span className="w-2 h-2 rounded-full mr-2 bg-current opacity-75"></span>
                    {getProductQuality(form.product).quality}
                  </span>
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Batch Code</label>
              <div className="flex gap-2">
                <select 
                  value={form.batchCodePrefix} 
                  onChange={(e) => handleBatchCodePrefixChange(e.target.value)}
                  className="w-20 rounded-lg border border-gray-200 px-3 py-2 text-sm dark:bg-navy-700 dark:text-white"
                >
                  <option value="">Prefix</option>
                  {batchCodePrefixes.map((prefix) => (
                    <option key={prefix} value={prefix}>
                      {prefix}
                    </option>
                  ))}
                </select>
                <input 
                  name="batchCode" 
                  value={form.batchCode} 
                  onChange={(e) => handleBatchCodeChange(e.target.value)}
                  placeholder="BN/25/33"
                  className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm dark:bg-navy-700 dark:text-white" 
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Supervisor</label>
              <select 
                name="supervisor" 
                value={form.supervisor} 
                onChange={handleInput} 
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:bg-navy-700 dark:text-white"
              >
                <option value="">Select a supervisor</option>
                {supervisorNames.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Analyst</label>
              <select 
                name="analyst" 
                value={form.analyst} 
                onChange={handleInput} 
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:bg-navy-700 dark:text-white"
              >
                <option value="">Select an analyst</option>
                {analystNames.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">No. of Monoblocks</label>
              <select 
                name="numMonoblocks" 
                value={form.numMonoblocks} 
                onChange={handleInput} 
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:bg-navy-700 dark:text-white"
              >
                <option value="">Select number of monoblocks</option>
                {Array.from({ length: 10 }, (_, i) => i + 1).map((num) => (
                  <option key={num} value={num}>
                    {num}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Analysis Section */}
          <div>
            <h2 className="text-lg font-semibold text-navy-700 dark:text-white mb-3 sm:mb-4">Analysis of Product</h2>
            
            {/* Count, Moisture, and Weight Input Fields (Optional) */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium mb-1">Count <span className="text-gray-500 text-xs">(Optional)</span></label>
                <div className="flex items-center gap-1">
                  <input 
                    name="count" 
                    value={form.count} 
                    onChange={handleInput} 
                    type="number" 
                    min="0"
                    step="0.01"
                    placeholder="Enter count value"
                    className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm dark:bg-navy-700 dark:text-white" 
                  />
                  {form.count && (
                    <span className="text-xs text-blue-600 dark:text-blue-400 font-medium whitespace-nowrap">
                      {(parseFloat(form.count) * 4).toFixed(2)}
                    </span>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Moisture (%) <span className="text-gray-500 text-xs">(Optional)</span></label>
                <input 
                  name="moisture" 
                  value={form.moisture} 
                  onChange={handleInput} 
                  type="number" 
                  min="0" 
                  max="100"
                  step="0.1"
                  placeholder="Enter moisture %"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:bg-navy-700 dark:text-white" 
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Weight (kg) <span className="text-gray-500 text-xs">(Optional)</span></label>
                <input 
                  name="weight" 
                  value={form.weight} 
                  onChange={handleInput} 
                  type="number" 
                  min="0"
                  step="0.01"
                  placeholder="Enter weight in kg"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:bg-navy-700 dark:text-white" 
                />
              </div>
            </div>

            {/* Analysis Table - Mobile Responsive */}
            <div className="mb-3">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                <strong>Note:</strong> Enter values in grams. The system will automatically convert to percentage using the formula: (grams ÷ 454) × 100
              </p>
            </div>
            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-navy-700">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-navy-700">
                <thead className="bg-gray-50 dark:bg-navy-800">
                  <tr>
                    <th className="px-2 sm:px-3 py-2 text-left text-xs font-medium text-gray-500">Quality</th>
                    <th className="px-2 sm:px-3 py-2 text-left text-xs font-medium text-gray-500">Grams</th>
                    <th className="px-2 sm:px-3 py-2 text-left text-xs font-medium text-gray-500">Defects</th>
                    <th className="px-2 sm:px-3 py-2 text-left text-xs font-medium text-gray-500">Grams</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-navy-900 divide-y divide-gray-100 dark:divide-navy-800">
                  {Array.from({ length: Math.max(qualityRows.length, defectRows.length) }).map((_, idx) => (
                    <tr key={idx}>
                      <td className="px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-navy-700 dark:text-white">
                        {qualityRows[idx] || ''}
                      </td>
                      <td className="px-2 sm:px-3 py-2">
                        {qualityRows[idx] !== undefined && (
                          <div className="flex items-center gap-1">
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              placeholder="0.00"
                              value={form.qualityPercentages[idx]}
                              onChange={e => handleQualityPercentageChange(idx, e.target.value)}
                              className="w-16 sm:w-20 rounded border border-gray-200 px-1 sm:px-2 py-1 text-xs sm:text-sm dark:bg-navy-700 dark:text-white"
                            />
                            {form.qualityPercentages[idx] && (
                              <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                                {((parseFloat(form.qualityPercentages[idx]) / 454) * 100).toFixed(2)}%
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-navy-700 dark:text-white">
                        {defectRows[idx] || ''}
                      </td>
                      <td className="px-2 sm:px-3 py-2">
                        {defectRows[idx] !== undefined && (
                          <div className="flex items-center gap-1">
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              placeholder="0.00"
                              value={form.defectPercentages[idx]}
                              onChange={e => handleDefectPercentageChange(idx, e.target.value)}
                              className="w-16 sm:w-20 rounded border border-gray-200 px-1 sm:px-2 py-1 text-xs sm:text-sm dark:bg-navy-700 dark:text-white"
                            />
                            {form.defectPercentages[idx] && (
                              <span className="text-xs text-purple-600 dark:text-purple-400 font-medium">
                                {((parseFloat(form.defectPercentages[idx]) / 454) * 100).toFixed(2)}%
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Recommendation */}
          <div>
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <h2 className="text-lg font-semibold text-navy-700 dark:text-white">Quality Assessment & Recommendation</h2>
              
              {/* Manual Override Toggle */}
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={manualOverride.enabled}
                    onChange={(e) => setManualOverride(prev => ({
                      ...prev,
                      enabled: e.target.checked,
                      status: e.target.checked ? prev.status : null,
                      reason: e.target.checked ? prev.reason : ''
                    }))}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                  />
                  Manual Override
                </label>
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-navy-800 dark:to-navy-700 rounded-xl p-6 border border-blue-100 dark:border-navy-600">
              <div className="space-y-6">
                {/* Recommendation Status */}
                <div className={`rounded-lg p-6 border-l-4 ${
                  recommendation.status === 'approved' 
                    ? 'bg-green-50 border-green-400 dark:bg-green-900/30 dark:border-green-500' :
                  recommendation.status === 'pending'
                    ? 'bg-gray-50 border-gray-400 dark:bg-gray-900/30 dark:border-gray-500' :
                    'bg-red-50 border-red-400 dark:bg-red-900/30 dark:border-red-500'
                }`}>
                  <div className="flex items-center gap-4">
                    <div className={`p-3 rounded-full ${
                      recommendation.status === 'approved' 
                        ? 'bg-green-100 dark:bg-green-800' :
                      recommendation.status === 'pending'
                        ? 'bg-gray-100 dark:bg-gray-800' :
                        'bg-red-100 dark:bg-red-800'
                    }`}>
                      {recommendation.status === 'approved' ? (
                        <MdCheckCircle className="text-2xl text-green-600 dark:text-green-400" />
                      ) : recommendation.status === 'pending' ? (
                        <MdAccessTime className="text-2xl text-gray-600 dark:text-gray-400" />
                      ) : (
                        <MdCancel className="text-2xl text-red-600 dark:text-red-400" />
                      )}
                    </div>
                    <div>
                      <h3 className={`text-lg font-bold ${
                        recommendation.status === 'approved' 
                          ? 'text-green-800 dark:text-green-200' :
                        recommendation.status === 'pending'
                          ? 'text-gray-800 dark:text-gray-200' :
                          'text-red-800 dark:text-red-200'
                      }`}>
                        {recommendation.status === 'approved' ? 'APPROVED' :
                         recommendation.status === 'pending' ? 'PENDING' : 'REJECTED'}
                      </h3>
                      <p className={`text-sm ${
                        recommendation.status === 'approved' 
                          ? 'text-green-700 dark:text-green-300' :
                        recommendation.status === 'pending'
                          ? 'text-gray-700 dark:text-gray-300' :
                          'text-red-700 dark:text-red-300'
                      }`}>
                        {recommendation.message}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Manual Override Controls */}
                {manualOverride.enabled && (
                  <div className="bg-white dark:bg-navy-800 rounded-lg p-6 border border-purple-200 dark:border-purple-700">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 rounded-full bg-purple-100 dark:bg-purple-900">
                        <svg className="w-5 h-5 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      </div>
                      <div>
                        <h4 className="text-lg font-semibold text-purple-800 dark:text-purple-200">
                          Manual Override Active
                        </h4>
                        <p className="text-sm text-purple-600 dark:text-purple-400">
                          Analyst can override automatic assessment
                        </p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      {/* Status Selection */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Select Final Status
                        </label>
                        <div className="flex gap-3">
                          <button
                            type="button"
                            onClick={() => setManualOverride(prev => ({ ...prev, status: 'approved' }))}
                            className={`flex-1 px-4 py-3 rounded-lg border-2 font-medium transition-all ${
                              manualOverride.status === 'approved'
                                ? 'border-green-500 bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                                : 'border-gray-200 bg-white text-gray-700 hover:border-green-300 hover:bg-green-50 dark:border-gray-600 dark:bg-navy-700 dark:text-gray-300 dark:hover:border-green-600'
                            }`}
                          >
                            <div className="flex items-center justify-center gap-2">
                              <MdCheckCircle className="text-xl" />
                              Approve
                            </div>
                          </button>
                          <button
                            type="button"
                            onClick={() => setManualOverride(prev => ({ ...prev, status: 'rejected' }))}
                            className={`flex-1 px-4 py-3 rounded-lg border-2 font-medium transition-all ${
                              manualOverride.status === 'rejected'
                                ? 'border-red-500 bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                                : 'border-gray-200 bg-white text-gray-700 hover:border-red-300 hover:bg-red-50 dark:border-gray-600 dark:bg-navy-700 dark:text-gray-300 dark:hover:border-red-600'
                            }`}
                          >
                            <div className="flex items-center justify-center gap-2">
                              <MdCancel className="text-xl" />
                              Reject
                            </div>
                          </button>
                        </div>
                      </div>

                      {/* Reason Input */}
                      {manualOverride.status && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Override Reason (Optional)
                          </label>
                          <textarea
                            value={manualOverride.reason}
                            onChange={(e) => setManualOverride(prev => ({ ...prev, reason: e.target.value }))}
                            placeholder="Explain why you're overriding the automatic assessment..."
                            rows={3}
                            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:bg-navy-700 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                          />
                        </div>
                      )}

                      {/* Auto Assessment Info */}
                      <div className="bg-gray-50 dark:bg-navy-700 rounded-lg p-4">
                        <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Automatic Assessment
                        </h5>
                        <div className="flex items-center gap-2">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            autoRecommendation.status === 'approved' 
                              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                            autoRecommendation.status === 'pending'
                              ? 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200' :
                              'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                          }`}>
                            {autoRecommendation.status.toUpperCase()}
                          </span>
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            {autoRecommendation.message}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Count Assessment Results */}
                {countAssessment.length > 0 && (
                  <div className="bg-white dark:bg-navy-800 rounded-lg p-6 border border-blue-200 dark:border-blue-800">
                    <h4 className="text-lg font-semibold text-blue-800 dark:text-blue-200 mb-4">
                      Count Assessment Results
                    </h4>
                    <div className="space-y-3">
                      {countAssessment.map((result, index) => (
                        <div key={index} className={`flex items-center justify-between p-3 rounded-lg border ${
                          result.status === 'passed' 
                            ? 'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-700' 
                            : 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-700'
                        }`}>
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                              result.status === 'passed' ? 'bg-green-100 dark:bg-green-900' : 'bg-red-100 dark:bg-red-900'
                            }`}>
                              <span className={`text-xs font-bold ${
                                result.status === 'passed' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                              }`}>
                                {result.status === 'passed' ? '✓' : '✗'}
                              </span>
                            </div>
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">{result.field}</p>
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                Count Assessment
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className={`text-lg font-bold ${
                              result.status === 'passed' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                            }`}>
                              {result.value}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              Threshold: {result.threshold}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Broken Assessment Results */}
                {brokenAssessment.length > 0 && (
                  <div className="bg-white dark:bg-navy-800 rounded-lg p-6 border border-purple-200 dark:border-purple-800">
                    <h4 className="text-lg font-semibold text-purple-800 dark:text-purple-200 mb-4">
                      Broken Assessment Results
                    </h4>
                    <div className="space-y-3">
                      {brokenAssessment.map((result, index) => (
                        <div key={index} className={`flex items-center justify-between p-3 rounded-lg border ${
                          result.status === 'passed' 
                            ? 'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-700' 
                            : 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-700'
                        }`}>
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                              result.status === 'passed' ? 'bg-green-100 dark:bg-green-900' : 'bg-red-100 dark:bg-red-900'
                            }`}>
                              <span className={`text-xs font-bold ${
                                result.status === 'passed' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                              }`}>
                                {result.status === 'passed' ? '✓' : '✗'}
                              </span>
                            </div>
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">{result.field}</p>
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                Broken Assessment
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className={`text-lg font-bold ${
                              result.status === 'passed' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                            }`}>
                              {result.value}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              Threshold: {result.threshold}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Failed Fields Details - Show for rejected status and not manually overridden to approved */}
                {recommendation.status === 'rejected' && recommendation.failedFields && recommendation.failedFields.length > 0 && !(manualOverride.enabled && manualOverride.status === 'approved') && (
                                      <div className="bg-white dark:bg-navy-800 rounded-lg p-6 border border-red-200 dark:border-red-800">
                      <h4 className="text-lg font-semibold text-red-800 dark:text-red-200 mb-4">
                        Failed Quality Checks
                      </h4>
                    <div className="space-y-3">
                      {recommendation.failedFields.map((field, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-red-50 dark:bg-red-900/30 rounded-lg border border-red-200 dark:border-red-700">
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                              field.type === 'quality' ? 'bg-blue-100 dark:bg-blue-900' : 'bg-orange-100 dark:bg-orange-900'
                            }`}>
                              <span className={`text-xs font-bold ${
                                field.type === 'quality' ? 'text-blue-600 dark:text-blue-400' : 'text-orange-600 dark:text-orange-400'
                              }`}>
                                {field.type === 'quality' ? 'Q' : 'D'}
                              </span>
                            </div>
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">{field.field}</p>
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                {field.type === 'quality' ? 'Quality' : 'Defect'} Field
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-red-600 dark:text-red-400">{field.value}</p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              Threshold: {field.threshold}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Rework */}
          <div>
            <h2 className="text-lg font-semibold text-navy-700 dark:text-white mb-3 sm:mb-4">Rework</h2>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="rework"
                  value="Yes"
                  checked={form.rework === 'Yes'}
                  onChange={(e) => handleRework(e.target.value)}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                />
                <span className="text-sm font-medium text-navy-700 dark:text-white">Yes</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="rework"
                  value="No"
                  checked={form.rework === 'No'}
                  onChange={(e) => handleRework(e.target.value)}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                />
                <span className="text-sm font-medium text-navy-700 dark:text-white">No</span>
              </label>
            </div>
          </div>

          {/* Observation */}
          <div>
            <label className="block text-sm font-medium mb-1">Observation</label>
            <textarea
              name="observation"
              value={form.observation}
              onChange={handleInput}
              rows={3}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:bg-navy-700 dark:text-white"
            />
          </div>

          {/* Footer Info & Signatures */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Date (mm/dd/yyyy)</label>
              <input name="date" value={form.date} onChange={handleInput} type="date" className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:bg-navy-700 dark:text-white" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Quality Manager</label>
              <div className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm bg-gray-50 dark:bg-navy-700 dark:text-white dark:border-navy-600">
                Gifty Asante
              </div>
            </div>
          </div>

          {/* Document issue footer */}
          <div className="text-center mt-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">Issue number: 7 — Date Issued: 30/09/2025</p>
          </div>



          {/* Submit Button */}
          <div className="flex justify-center sm:justify-end pt-4">
            <button 
              type="submit" 
              disabled={isSubmitting}
              className={`w-full sm:w-auto rounded-lg px-6 py-3 text-white font-semibold transition-colors text-base sm:text-sm flex items-center justify-center gap-2 ${
                isSubmitting 
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Submitting...
                </>
              ) : (
                'Submit Analysis'
              )}
            </button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default AnalystEntry2; 